"use client";

import { BASKET_LIMITS } from "@repo/embed";
import { toast } from "@ui/components/toast";
import { isToolUIPart, type UIMessage } from "ai";
import {
	useCallback,
	useEffect,
	useEffectEvent,
	useMemo,
	useRef,
	useState,
} from "react";
import type { BasketDiff } from "./bridge";
import { debugLog } from "./debug";
import {
	type Basket,
	type BasketError,
	type BasketItemInput,
	type BasketLine,
	type BasketPick,
	basketFrom,
	type CartHandoff,
} from "./types";
import type { ShopperSession } from "./use-shopper-session";

/** The head of an empty queue: shared, a settled promise never changes. */
const SETTLED: Promise<unknown> = Promise.resolve();

/**
 * The shopper's basket: mirrored from basket tool outputs (no refetch), changed by the
 * steppers (PUT, by our product id) or the host's cart snapshot (by its ids). Every diff the
 * server returns goes to `onDiff`, which turns it into bridge events. `idle` is false while a
 * turn runs or the conversation is still loading: a host snapshot waits for it. A snapshot
 * with items and no conversation yet opens one with `opening` (store, locale, restrictions),
 * so the app's cart shows as the chat opens.
 *
 * Writes and checkout run one at a time in the order asked (`inOrder`): a slow write's answer
 * can never land after a newer one and undo it, and checkout hands over the basket after every
 * change before it (2026-10-19: taps during a slow write lost changes, and checkout could miss
 * the last one). Our own chain, not a TanStack Query mutation scope: a scope resumes a queued
 * mutation only while the page counts as focused, and a webview under the app's own cart is
 * hidden and fired no `visibilitychange` on return, so every tap after a cart round trip
 * waited for good (2026-10-20).
 */
export function useBasket({
	session,
	messages,
	idle,
	opening,
	onDiff,
}: {
	session: ShopperSession;
	messages: UIMessage[];
	idle: boolean;
	opening: () => object;
	onDiff: (diff: BasketDiff, origin: "shopper" | "host") => void;
}) {
	const [basket, setBasket] = useState<Basket | null>(null);

	// Basket tools return the whole basket; mirror the latest one, once. Mirrored again on
	// every message, an old output would undo every change made since without a tool: the
	// steppers, "Add all", the host's snapshot (the bar fell back to the agent's last add).
	const mirrored = useRef<string | null>(null);
	useEffect(() => {
		const latest = latestBasketCall(messages);
		if (latest && latest.toolCallId !== mirrored.current) {
			mirrored.current = latest.toolCallId;
			setBasket(latest.basket);
		}
	}, [messages]);

	/** Restored history comes with its current basket: none of its tool outputs is newer. */
	const restored = useCallback((history: UIMessage[]) => {
		mirrored.current = latestBasketCall(history)?.toolCallId ?? null;
	}, []);

	// The tail of the queue; a failed call does not stop the ones after it.
	const queue = useRef(SETTLED);
	const queued = useRef(0);
	const inOrder = useCallback(<T>(call: () => Promise<T>): Promise<T> => {
		if (queued.current > 0) {
			debugLog(
				`basket: waits for ${queued.current} request(s) before it`,
			);
		}
		queued.current += 1;
		const run = queue.current.then(call, call).finally(() => {
			queued.current -= 1;
		});
		queue.current = run.catch(() => undefined);
		return run;
	}, []);

	const putItems = useCallback(
		({
			conversationId,
			items,
			origin,
		}: {
			conversationId: string;
			items: BasketItemInput[];
			origin: "shopper" | "host";
		}) =>
			inOrder(async () =>
				okJson<
					BasketDiff & {
						basket: Basket;
						errors: BasketError[];
					}
				>(
					await session.request(
						`/conversations/${conversationId}/basket`,
						{
							method: "PUT",
							headers: { "content-type": "application/json" },
							body: JSON.stringify({ items, origin }),
						},
					),
				),
			),
		[session, inOrder],
	);
	const emptyForHost = useCallback(
		(conversationId: string) =>
			inOrder(async () =>
				okJson<BasketDiff & { basket: Basket }>(
					await session.request(
						`/conversations/${conversationId}/basket?origin=host`,
						{ method: "DELETE" },
					),
				),
			),
		[session, inOrder],
	);
	const prepareCheckout = useCallback(
		(conversationId: string) =>
			inOrder(async () =>
				okJson<{ cart: CartHandoff; eventId: string | null }>(
					await session.request(
						`/conversations/${conversationId}/checkout`,
						{
							method: "POST",
						},
					),
				),
			),
		[session, inOrder],
	);

	const quantities = useMemo(
		() =>
			new Map(
				basket?.lines.map((line) => [line.productId, line.quantity]),
			),
		[basket],
	);
	const itemCount = basket?.itemCount ?? 0;

	const put = useCallback(
		async (
			items: BasketItemInput[],
			origin: "shopper" | "host" = "shopper",
		) => {
			const conversationId = session.conversationId();
			const data = conversationId
				? await putItems({ conversationId, items, origin }).catch(
						() => null,
					)
				: null;
			const shopper = origin === "shopper";
			if (!data) {
				if (shopper) {
					toast.add({
						type: "error",
						title: "Couldn't update your basket",
						description: "Check your connection and try again.",
					});
				}
				return false;
			}
			if (shopper && data.basket.itemCount > itemCount) {
				// A small tick on phones that support it, like the app's own add.
				navigator.vibrate?.(8);
			}
			setBasket(data.basket);
			onDiff(data, origin);
			const [error] = data.errors;
			if (error) {
				if (shopper) {
					toast.add({
						type: "warning",
						title:
							error.error === "out_of_stock"
								? "That item just went out of stock"
								: error.error === "over_max_quantity"
									? `You can order up to ${error.maxOrderQuantity} of this`
									: "Couldn't add that item",
					});
				}
				return false;
			}
			return true;
		},
		[session, putItems, itemCount, onDiff],
	);

	/**
	 * The host's cart.snapshot (ADR-6), the app's whole cart: each listed item's quantity is
	 * set, a line of `before` it no longer lists is removed, and an empty snapshot empties the
	 * basket. `before` is the basket as the app sent it: a line the agent added while the
	 * snapshot waited for its turn to end is not the app's to remove. Sent in chunks the basket
	 * route takes, quantities capped at its maximum. False when part of it did not apply
	 * (logged: the host gets no reply).
	 */
	const syncHost = useCallback(
		async (items: HostItem[], before: BasketLine[]) => {
			const conversationId = session.conversationId();
			if (!conversationId) {
				return false;
			}
			let applied = true;
			if (items.length === 0) {
				const data = await emptyForHost(conversationId).catch(
					() => null,
				);
				if (data) {
					setBasket(data.basket);
					onDiff(data, "host");
				} else {
					applied = false;
				}
			}
			const listed = new Set(
				items.map(({ itemId, catalogId }) =>
					itemId ? `item:${itemId}` : `catalog:${catalogId}`,
				),
			);
			const removed: BasketItemInput[] = items.length
				? before
						.filter(
							(line) =>
								!listed.has(`item:${line.itemId}`) &&
								!listed.has(`catalog:${line.externalId}`),
						)
						.map((line) => ({
							productId: line.productId,
							quantity: 0,
						}))
				: [];
			const changes: BasketItemInput[] = [
				...items.map(({ itemId, catalogId, quantity }) => ({
					// The bridge schema lets through exactly one of the two.
					...(itemId ? { itemId } : { catalogId: catalogId ?? "" }),
					quantity: Math.min(quantity, BASKET_LIMITS.quantity),
				})),
				...removed,
			];
			// Every chunk joins the write queue now, back to back, so no other write lands
			// between two parts of one snapshot; the queue still runs them one at a time.
			const chunks: BasketItemInput[][] = [];
			for (let i = 0; i < changes.length; i += BASKET_LIMITS.items) {
				chunks.push(changes.slice(i, i + BASKET_LIMITS.items));
			}
			const results = await Promise.all(
				chunks.map((chunk) => put(chunk, "host")),
			);
			applied = results.every(Boolean) && applied;
			if (!applied) {
				// biome-ignore lint/suspicious/noConsole: the host's developers read the webview console
				console.warn("Hephon: a cart.snapshot did not fully apply");
			}
			return applied;
		},
		[session, put, emptyForHost, onDiff],
	);

	/** A conversation for the app's cart to land in, before the shopper's first message. */
	const open = useCallback(async () => {
		const res = await session
			.request("/conversations", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify(opening()),
			})
			.catch(() => null);
		// The session keeps the id from the response's x-conversation-id.
		return Boolean(res?.ok && session.conversationId());
	}, [session, opening]);

	// The host's latest snapshot and the basket it was sent against, held until the
	// conversation is loaded and no turn runs. `received` counts the snapshots: each one
	// runs the effect below, which applies the one held once the chat is idle.
	const held = useRef<{ items: HostItem[]; before: BasketLine[] } | null>(
		null,
	);
	const [received, setReceived] = useState(0);
	const opened = useRef<Promise<boolean> | null>(null);
	const flush = useEffectEvent(() => {
		const snapshot = held.current;
		if (!snapshot || !idle) {
			return;
		}
		if (session.conversationId()) {
			held.current = null;
			void syncHost(snapshot.items, snapshot.before);
		} else if (snapshot.items.length > 0 && !opened.current) {
			opened.current = open().then((ok) => {
				opened.current = null;
				if (ok) {
					flush();
				} else {
					// biome-ignore lint/suspicious/noConsole: the host's developers read the webview console
					console.warn(
						"Hephon: could not open a conversation for the cart",
					);
				}
				return ok;
			});
		}
	});
	useEffect(() => {
		if (idle && received > 0) {
			flush();
		}
	}, [idle, received]);

	/**
	 * A cart.snapshot from the host. An empty one before there is a conversation has nothing
	 * to empty, so it is dropped: held, it would empty the first turn's basket (the item the
	 * agent just added) once that turn ends. One with items opens the conversation.
	 */
	const hostSnapshot = (items: HostItem[]) => {
		debugLog(
			`cart.snapshot: ${items.length} item(s)${idle ? "" : ", held until the reply ends"}`,
		);
		held.current =
			items.length > 0 || session.conversationId()
				? { items, before: basket?.lines ?? [] }
				: null;
		setReceived((count) => count + 1);
	};

	const setQuantity = useCallback(
		(productId: string, quantity: number) => put([{ productId, quantity }]),
		[put],
	);

	/** Each pick at its quantity at least: what the shopper already has is never lowered. */
	const addAll = useCallback(
		(picks: BasketPick[]) =>
			put(
				picks.map(({ product, quantity }) => ({
					productId: product.id,
					quantity: Math.max(
						quantity,
						quantities.get(product.id) ?? 0,
					),
				})),
			),
		[put, quantities],
	);

	/** The basket in the retailer's ids, announced to its webhooks; null when it failed. */
	const checkout = useCallback(async () => {
		const conversationId = session.conversationId();
		const result = conversationId
			? await prepareCheckout(conversationId).catch(() => null)
			: null;
		if (!result) {
			debugLog(
				conversationId
					? "checkout: the request failed"
					: "checkout: no conversation yet",
			);
			toast.add({
				type: "error",
				title: "Couldn't prepare your checkout",
				description: "Try again in a moment.",
			});
			return null;
		}
		return result;
	}, [session, prepareCheckout]);

	return {
		basket,
		setBasket,
		restored,
		quantities,
		hostSnapshot,
		setQuantity,
		addAll,
		checkout,
	};
}

/** The last finished tool call that returned a basket. */
function latestBasketCall(
	messages: UIMessage[],
): { toolCallId: string; basket: Basket } | null {
	for (const message of [...messages].reverse()) {
		for (const part of [...message.parts].reverse()) {
			if (isToolUIPart(part) && part.state === "output-available") {
				const basket = basketFrom(part.output);
				if (basket) {
					return { toolCallId: part.toolCallId, basket };
				}
			}
		}
	}
	return null;
}

/** A cart.snapshot line: by its item id in this store, or by its catalog id. */
interface HostItem {
	itemId?: string | undefined;
	catalogId?: string | undefined;
	quantity: number;
}

/** The body of a successful answer; anything else throws, so the mutation fails. */
async function okJson<T>(res: Response): Promise<T> {
	if (!res.ok) {
		throw new Error(`Basket request failed: ${res.status}`);
	}
	return (await res.json()) as T;
}
